import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

interface SimpleQueryParamsArgs<T> {
  defaultQuery: T
  setDefaultQuery?: boolean
}

export const useSimpleQueryParams = <T extends Record<string, any>>({
  defaultQuery,
  setDefaultQuery = false,
}: SimpleQueryParamsArgs<T>): [T, (arg: Partial<T>) => void] => {
  const navigate = useNavigate()
  const location = useLocation()

  const setQueryParams = useCallback(
    (query: Record<string, any>) => {
      const searchParams = new URLSearchParams(window.location.search)
      for (const [key, value] of Object.entries(query)) {
        // null removes the key. undefined still writes an empty value, which existing callers rely on.
        if (value === null) {
          searchParams.delete(key)
          continue
        }

        searchParams.set(key, value ?? '')
      }

      navigate(`?${searchParams.toString()}`, { replace: true })
    },
    [navigate],
  )

  // Initialize missing default params once if required
  useEffect(() => {
    if (!setDefaultQuery) {
      return
    }

    const queryParams = new URLSearchParams(window.location.search)
    const missingInQuery = Object.keys(defaultQuery).filter((key) => !queryParams.has(key))

    const missing: Record<string, any> = {}
    for (const k of missingInQuery) {
      missing[k] = defaultQuery[k] ?? ''
    }

    if (Object.keys(missing).length > 0) {
      setQueryParams(missing)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // intentionally mount-only: defaults are initialized once, not reset on re-render

  const queryResult = useMemo(() => {
    // We use window.location here because location from useLocation does not
    // update fast enough after history.replace — we end up with missing keys.
    const queryParams = new URLSearchParams(window.location.search)

    return {
      ...defaultQuery,
      ...Object.fromEntries(queryParams),
    } as T
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  return [queryResult, setQueryParams]
}
